/**
 * Green Brothers — header compression on scroll.
 * Turns black while overlapping the lunar proof section.
 */
(() => {
  const header = document.querySelector('#header-component');
  const group = document.querySelector('#header-group');
  const section = document.querySelector('.header-section');
  const targets = [header, group, section].filter(Boolean);
  if (!header || !targets.length) return;

  const proof = document.querySelector('.section-gb-proof, #preuve');
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobile = window.matchMedia('(max-width: 749px)');
  const range = 80;
  let ticking = false;

  const overlapsProof = () => {
    if (!proof) return false;
    const hr = header.getBoundingClientRect();
    const pr = proof.getBoundingClientRect();
    return pr.top < hr.bottom && pr.bottom > hr.top;
  };

  const apply = () => {
    const y = Math.max(0, window.scrollY || window.pageYOffset || 0);
    const t = motion.matches ? (y > 8 ? 1 : 0) : Math.min(1, y / range);
    const compact = t > 0.45;
    const scrolled = mobile.matches ? y > 16 : t > 0.05;
    const onProof = overlapsProof();
    targets.forEach((el) => {
      el.style.setProperty('--gb-header-t', t.toFixed(3));
      el.classList.toggle('gb-header--compact', compact);
      el.classList.toggle('gb-header--scrolled', scrolled);
      el.classList.toggle('gb-header--on-proof', onProof);
    });
    ticking = false;
  };

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(apply);
  };

  const menu = header.querySelector('.gb-mobile-nav__details');
  const summary = menu?.querySelector('summary');
  const panel = menu?.querySelector('.gb-mobile-nav__panel');
  let targetOpen = Boolean(menu?.open);
  let sequence = 0;
  let animations = [];
  const background = [...document.querySelectorAll('#MainContent, #footer-group')];
  const previousInert = new Map();

  const lockBackground = (locked) => {
    background.forEach((el) => {
      if (locked) {
        if (!previousInert.has(el)) previousInert.set(el, el.inert);
        el.inert = true;
      } else if (previousInert.has(el)) {
        el.inert = previousInert.get(el);
        previousInert.delete(el);
      }
    });
  };

  const setMenu = (open, { animate = true, focus = false } = {}) => {
    if (!menu || !panel || !summary) return;
    const current = getComputedStyle(panel);
    const from = menu.open ? { opacity: current.opacity, transform: current.transform } : { opacity: 0, transform: 'translateY(-12px)' };
    const token = ++sequence;
    animations.forEach((animation) => animation.cancel());
    animations = [];
    targetOpen = open;
    summary.toggleAttribute('data-instant', !animate || motion.matches);
    summary.setAttribute('aria-expanded', String(open));
    targets.forEach((el) => el.classList.toggle('gb-header--menu-open', open));
    const finish = () => {
      if (token !== sequence) return;
      menu.open = open;
      panel.inert = !open;
      lockBackground(open);
      if (focus) summary.focus({ preventScroll: true });
      apply();
    };
    if (open) {
      menu.open = true;
      panel.inert = false;
      lockBackground(true);
    } else {
      // Links and background unlock before native anchor navigation.
      panel.inert = true;
      lockBackground(false);
      if (focus) summary.focus({ preventScroll: true });
    }
    if (!animate || motion.matches || !mobile.matches || !panel.animate) {
      finish();
      return;
    }
    const animation = panel.animate([from, { opacity: open ? 1 : 0, transform: open ? 'translateY(0)' : 'translateY(-8px)' }], {
      duration: open ? 260 : 160,
      easing: 'cubic-bezier(0.23, 1, 0.32, 1)',
    });
    animations.push(animation);
    if (open) panel.querySelectorAll('.menu-list__list-item, .gb-vote-btn--drawer').forEach((item, index) => {
      animations.push(item.animate([{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'translateY(0)' }], {
        duration: 220, delay: Math.min(index * 18, 72), fill: 'backwards', easing: 'cubic-bezier(0.23, 1, 0.32, 1)',
      }));
    });
    animation.finished.then(finish).catch(() => {});
  };

  if (menu && summary && panel) {
    summary.setAttribute('aria-expanded', String(targetOpen));
    panel.inert = !targetOpen;
    summary.addEventListener('click', (event) => {
      event.preventDefault();
      setMenu(!targetOpen, { animate: event.detail > 0, focus: targetOpen });
    });
    // Also support external/native changes to details, including editor refreshes.
    menu.addEventListener('toggle', () => {
      if (menu.open !== targetOpen && !animations.some((a) => a.playState === 'running')) {
        setMenu(menu.open, { animate: false });
      }
    });
  }
  document.addEventListener('click', (event) => {
    if (event.target instanceof Element && event.target.closest('.gb-mobile-nav__panel a[href]')) {
      setMenu(false, { animate: false });
    }
  });
  document.addEventListener('keydown', (event) => {
    if (!targetOpen) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      setMenu(false, { animate: false, focus: true });
    } else if (event.key === 'Tab') {
      const controls = [...header.querySelectorAll('a[href], button, summary, input, select, [tabindex="0"]')]
        .filter((el) => !el.disabled && !el.closest('[inert]') && el.getClientRects().length);
      const first = controls[0], last = controls.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }
  });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) setMenu(false, { animate: false });
  });
  motion.addEventListener('change', () => {
    setMenu(targetOpen, { animate: false });
    onScroll();
  });

  apply();
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', () => {
    if (!mobile.matches) setMenu(false, { animate: false });
    onScroll();
  }, { passive: true });
  window.addEventListener('hashchange', apply);
})();
